import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { z } from 'zod';
import { Input } from '@components/forms/Input';
import { FormCard } from '@components/forms/FormCard';
import { Button } from '@components/common/Button';
import { ErrorAlert } from '@components/common/ErrorAlert';
import { useLoginMutation } from '@store/authApi';
import { useDispatch, useSelector } from 'react-redux';
import { setCredentials } from '@store/authSlice';
import {
  extractErrorMessage,
  extractFieldErrors,
  logError,
  isValidationError,
} from '@utils/errorHandling';
import { LogIn, User, Lock } from 'lucide-react';
import type { RootState } from '@store/index';
import { mockLogin } from '@mocks/users';

const loginSchema = z.object({
  email: z.string().min(1, 'Email is required').email('Please enter a valid email address'),
  password: z
    .string()
    .min(1, 'Password is required')
    .min(3, 'Password must be at least 3 characters'),
});

type LoginFormData = z.infer<typeof loginSchema>;

export const Login: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const [loginUser, { isLoading }] = useLoginMutation();
  const [apiError, setApiError] = useState<string>('');
  const { isAuthenticated, user } = useSelector((state: RootState) => state.auth);

  // Redirect authenticated users
  useEffect(() => {
    if (isAuthenticated && user) {
      // Route based on user role
      switch (user.role) {
        case 'APPLICANT':
          navigate('/dashboard', { replace: true });
          break;
        case 'ADMIN':
        case 'OPS':
          navigate('/admin', { replace: true });
          break;
        default:
          navigate('/dashboard', { replace: true });
      }
    }
  }, [isAuthenticated, user, navigate]);

  // Handle session expiry or other navigation messages
  useEffect(() => {
    const state = location.state as { message?: string; from?: string } | null;
    if (state?.message) {
      setApiError(state.message);
      // Clear the navigation state
      window.history.replaceState(null, '', location.pathname);
    }
  }, [location]);

  const {
    register,
    handleSubmit,
    formState: { errors },
    setError,
    clearErrors,
  } = useForm<LoginFormData>({
    resolver: zodResolver(loginSchema),
    defaultValues: {
      email: '',
      password: '',
    },
    mode: 'onSubmit', // Only validate on form submission
  });

  const onSubmit = async (data: LoginFormData) => {
    try {
      // Clear previous errors
      setApiError('');
      clearErrors();

      // ========== MOCK AUTHENTICATION ==========
      // Use mock login instead of API call
      const mockUser = mockLogin(data.email, data.password);

      if (!mockUser) {
        setApiError('Invalid email or password');
        return;
      }

      // Simulate API delay
      await new Promise((resolve) => setTimeout(resolve, 800));

      // Set credentials in Redux store (mock token)
      dispatch(
        setCredentials({
          user: mockUser,
          access_token: `mock_token_${mockUser.id}_${Date.now()}`,
        })
      );

      // Route based on user role
      switch (mockUser.role) {
        case 'APPLICANT':
          navigate('/dashboard');
          break;
        case 'ADMIN':
        case 'OPS':
          navigate('/admin');
          break;
        default:
          navigate('/dashboard');
      }
      // ========== END MOCK AUTHENTICATION ==========

      /* REAL API AUTHENTICATION (commented out for mock mode)
      const loginData = {
        username: data.email,
        password: data.password,
      };
      
      const res = await loginUser(loginData).unwrap();
      dispatch(setCredentials({ user: res.user, access_token: res.access_token }));
      
      // Route based on user role
      switch (res.user.role) {
        case 'APPLICANT':
          navigate('/dashboard');
          break;
        case 'ADMIN':
        case 'OPS':
          navigate('/admin');
          break;
        default:
          navigate('/dashboard');
      }
      */
    } catch (err: any) {
      // Log error for debugging
      logError(err, 'Login Error');

      // Handle validation errors (422)
      if (isValidationError(err)) {
        const fieldErrors = extractFieldErrors(err);

        Object.entries(fieldErrors).forEach(([field, message]) => {
          // Map backend field names to frontend field names
          let formField = field;
          if (field === 'username') {
            formField = 'email'; // Map username errors to email field
          }

          if (formField === 'email' || formField === 'password') {
            setError(formField as keyof LoginFormData, { message });
          } else {
            setApiError(`${field}: ${message}`);
          }
        });

        // If no field-specific errors, show general validation message
        if (Object.keys(fieldErrors).length === 0) {
          setApiError(extractErrorMessage(err));
        }
      } else {
        // Handle other errors (401, 500, etc.)
        setApiError(extractErrorMessage(err));
      }
    }
  };

  const handleRetry = () => {
    setApiError('');
    clearErrors();
  };

  const handleDismissError = () => {
    setApiError('');
  };

  return (
    <div className="min-h-screen flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full">
        <div className="text-center mb-8">
          <div className="w-16 h-16 bg-ncaa-primary text-white rounded-full flex items-center justify-center mx-auto mb-4">
            <LogIn className="h-8 w-8" />
          </div>
          <h2 className="text-3xl font-bold text-ncaa-dark">Sign In</h2>
          <p className="mt-2 text-sm text-gray-600">Access your DCEV Portal account</p>
        </div>

        <FormCard
          title="Welcome Back"
          description="Enter your credentials to continue"
          icon={<User className="h-5 w-5" />}
        >
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
            {apiError && (
              <ErrorAlert
                title="Login Failed"
                message={apiError}
                variant="error"
                onRetry={handleRetry}
                onDismiss={handleDismissError}
                retryLabel="Try Again"
              />
            )}

            <Input
              label="Email Address"
              type="email"
              required
              {...register('email')}
              error={errors.email?.message}
              placeholder="you@example.com"
              icon={<User className="h-5 w-5" />}
            />

            <Input
              label="Password"
              type="password"
              required
              {...register('password')}
              error={errors.password?.message}
              placeholder="Enter your password"
              icon={<Lock className="h-5 w-5" />}
            />

            <Button type="submit" fullWidth loading={isLoading}>
              Sign In
            </Button>

            <div className="text-center space-y-2">
              <div className="text-sm text-gray-600">
                Don't have an account?{' '}
                <Link to="/register" className="text-ncaa-primary font-medium hover:underline">
                  Create one here
                </Link>
              </div>
              <div className="space-y-1">
                <div>
                  <Link to="/forgot-password" className="text-sm text-ncaa-primary hover:underline">
                    Forgot your password?
                  </Link>
                </div>
                <div>
                  <Link to="/terms" className="text-sm text-gray-500 hover:underline">
                    Terms and Conditions
                  </Link>
                </div>
              </div>
            </div>
          </form>
        </FormCard>

        {/* Mock Mode Test Credentials */}
        <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
          <h3 className="text-sm font-semibold text-blue-900 mb-3 flex items-center gap-2">
            <User className="h-4 w-4" />
            Test Credentials (Staging Only)
          </h3>
          <div className="space-y-3 text-xs text-blue-800">
            <div className="bg-white p-3 rounded border border-blue-100">
              <p className="font-semibold mb-1">👤 Applicant</p>
              <p className="font-mono">applicant@test.com</p>
              <p className="font-mono">applicant123</p>
            </div>
            <div className="bg-white p-3 rounded border border-blue-100">
              <p className="font-semibold mb-1">👨‍💼 Admin - GM licensing </p>
              <p className="font-mono">admin@ncaa.gov.ng</p>
              <p className="font-mono">admin123</p>
            </div>
            <div className="bg-white p-3 rounded border border-blue-100">
              <p className="font-semibold mb-1">🔧 Supervisors</p>
              <p className="font-mono">operator@ncaa.gov.ng</p>
              <p className="font-mono">operator123</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
